import { sortBy } from 'lodash/fp';
import { BaseFieldProps, Field, InjectedFormProps, reduxForm } from 'redux-form';

import { Address } from 'dashboard/components/AddressInput/AddressInput';
import AddressInputField from 'dashboard/components/FormControls/AddressInputField';
import DateInputField from 'dashboard/components/FormControls/DateInputField';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InputField from 'dashboard/components/FormControls/InputField';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import ThemedMaskedInput, { ThemedMaskedInputProps } from 'dashboard/components/ThemedMaskedInput';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { isMaxLength, isRequired, isValidDate, withMessage } from 'lib/utils/formValidation';

const COUNTRY_CODE_OPTIONS = sortBy(
  'label',
  (Object.keys(COUNTRIES) as CountryCode[]).map((countryCode) => ({
    value: countryCode,
    label: COUNTRIES[countryCode],
  })),
);

const validate = {
  [PiiAttributes.Address]: [withMessage('Please enter a full address', isRequired)],
  [PiiAttributes.Birthdate]: [withMessage('Enter a valid date', isValidDate)],
  [PiiAttributes.CountryCode]: [withMessage('Country code is required', isRequired)],
  [PiiAttributes.NameFirst]: [withMessage('Please enter a first name', isRequired)],
  [PiiAttributes.NameLast]: [withMessage('Please enter a last name', isRequired)],
  'reference-id': [
    withMessage('Please limit your Reference ID to 255 characters', isMaxLength(255)),
  ],
  [PiiAttributes.SocialSecurityNumber]: [withMessage('Please enter a SSN', isRequired)],
};

interface FormCreateReportSyntheticProps {
  hideReferenceId: boolean;
  handleCancel: () => void;
  readOnly?: boolean;
}

const FormCreateReportSynthetic = ({
  submitting,
  hideReferenceId,
  handleSubmit,
  handleCancel,
  readOnly,
}: FormCreateReportSyntheticProps &
  InjectedFormProps<FormCreateReportSyntheticData, FormCreateReportSyntheticProps>) => {
  return (
    <Form onSubmit={handleSubmit}>
      {!hideReferenceId && (
        <Field
          id='create-report-synthetic__reference-id'
          label='Reference ID (Optional)'
          name='reference-id'
          disabled={submitting || readOnly}
          component={InputField}
          validate={validate['reference-id']}
          data-test='create-report-synthetic__reference-id'
        />
      )}
      <FormControlLabel>Name</FormControlLabel>
      <FormSection cols={2}>
        <Field
          id='create-report-synthetic__name-first'
          name={PiiAttributes.NameFirst}
          component={InputField}
          disabled={submitting || readOnly}
          validate={validate[PiiAttributes.NameFirst]}
          data-test='create-report-synthetic__name-first'
          placeholder='First name'
        />
        <Field
          id='create-report-synthetic__name-last'
          name={PiiAttributes.NameLast}
          component={InputField}
          disabled={submitting || readOnly}
          validate={validate[PiiAttributes.NameLast]}
          data-test='create-report-synthetic__name-last'
          placeholder='Last name'
        />
      </FormSection>
      <Field
        id='create-report-synthetic__birthdate'
        label='Birthdate'
        name={PiiAttributes.Birthdate}
        disabled={submitting || readOnly}
        component={DateInputField}
        validate={validate[PiiAttributes.Birthdate]}
        data-test='create-report-synthetic__birthdate'
      />
      <Field<ThemedMaskedInputProps & BaseFieldProps>
        id='create-report-synthetic__social-security-number'
        label='Social Security number'
        name={PiiAttributes.SocialSecurityNumber}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        component={ThemedMaskedInput}
        disabled={submitting || readOnly}
        mask={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
        type='text'
        placeholder='###-##-####'
        data-test='create-report-synthetic__social-security-number'
        validate={validate['social-security-number']}
      />
      <FormSection cols={1}>
        <Field
          id='create-report-synthetic__address'
          label='Address'
          name={PiiAttributes.Address}
          type='text'
          disabled={submitting || readOnly}
          component={AddressInputField}
          validate={validate[PiiAttributes.Address]}
          placeholder='Address'
          data-test='create-report-synthetic__address'
        />
      </FormSection>
      <Field
        id='create-report-synthetic__country-code'
        label='Country'
        component={ReactSelectField}
        name={PiiAttributes.CountryCode}
        data-test='create-report-synthetic__country-code'
        disabled={submitting || readOnly}
        validate={validate[PiiAttributes.CountryCode]}
        placeholder='Country'
        options={COUNTRY_CODE_OPTIONS}
        portal={true}
      />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='create-report-synthetic__cancel'
          data-test='create-report-synthetic__cancel'
          disabled={submitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='create-report-synthetic__submit'
          disabled={submitting}
          isPending={submitting}
        >
          Create Report
        </Button>
      </HStack>
    </Form>
  );
};

export interface FormCreateReportSyntheticData {
  'reference-id'?: string;
  [PiiAttributes.Address]?: Address;
  [PiiAttributes.Birthdate]?: string;
  [PiiAttributes.CountryCode]?: string;
  [PiiAttributes.NameFirst]?: string;
  [PiiAttributes.NameLast]?: string;
  [PiiAttributes.SocialSecurityNumber]?: string;
}

export const FORM_CREATE_REPORT_SYNTHETIC_NAME = 'formCreateReportSyntheticData';

export default reduxForm<FormCreateReportSyntheticData, FormCreateReportSyntheticProps>({
  form: FORM_CREATE_REPORT_SYNTHETIC_NAME,
})(FormCreateReportSynthetic);
